import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  LogOut, Dumbbell, Salad, TrendingUp, CreditCard, Home, Download, Plus,
  Calendar, Clock, Weight, Ruler, FileText, ChevronRight, AlertCircle, CheckCircle, ExternalLink, User, Save, Camera
} from "lucide-react";
import { ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import WorkoutLogTable from "@/components/WorkoutLogTable";
import PushOptIn from "@/components/PushOptIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-transparent.png";

function clientApi() {
  const token = localStorage.getItem("gg_client_token") || sessionStorage.getItem("gg_client_token");
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
}

export default function ClientDashboard() {
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const clientName = localStorage.getItem("gg_client_name") || sessionStorage.getItem("gg_client_name") || "Member";
  const token = localStorage.getItem("gg_client_token") || sessionStorage.getItem("gg_client_token");

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await clientApi().get("/client/dashboard");
      setDashboard(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("gg_client_token");
        localStorage.removeItem("gg_client_name");
        sessionStorage.removeItem("gg_client_token");
        sessionStorage.removeItem("gg_client_name");
        navigate("/login");
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchDashboard();
  }, [navigate, fetchDashboard, token]);

  const handleLogout = () => {
    localStorage.removeItem("gg_client_token");
    localStorage.removeItem("gg_client_name");
    localStorage.removeItem("gg_client_session");
    sessionStorage.removeItem("gg_client_token");
    sessionStorage.removeItem("gg_client_name");
    navigate("/login");
  };

  if (loading) {
    return <div className="min-h-screen bg-gg-black flex items-center justify-center"><div className="w-8 h-8 border-2 border-gg-red border-t-transparent rounded-full animate-spin" /></div>;
  }

  const tabs = [
    { id: "overview", label: "Home", icon: Home },
    { id: "workout", label: "Workout", icon: Dumbbell },
    { id: "log", label: "Log", icon: ClipboardList },
    { id: "diet", label: "Diet", icon: Salad },
    { id: "progress", label: "Progress", icon: TrendingUp },
    { id: "payments", label: "Pay", icon: CreditCard },
    { id: "profile", label: "Profile", icon: User },
  ];

  return (
    <div className="min-h-screen bg-gg-black" data-testid="client-dashboard">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-gg-black/90 backdrop-blur border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO_URL} alt="GG Fitness" className="h-8" />
          <div className="flex items-center gap-3">
            <span className="text-zinc-400 text-xs hidden sm:block">Hi, {clientName}</span>
            <PushOptIn />
            <RouterLink to="/home" data-testid="client-view-site" className="text-zinc-500 hover:text-white text-xs font-heading uppercase tracking-widest">View Site</RouterLink>
            <Button size="sm" variant="ghost" onClick={handleLogout} className="text-zinc-400 hover:text-white" data-testid="client-logout">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-[52px] z-30 bg-gg-black/90 backdrop-blur border-b border-white/5 overflow-x-auto scrollbar-none">
        <div className="max-w-6xl mx-auto px-4 flex gap-0">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-3 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap border-b-2 transition-all shrink-0 ${
                tab === t.id ? "border-gg-red text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}>
              <t.icon size={13} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {tab === "overview" && <OverviewTab dashboard={dashboard} setTab={setTab} />}
        {tab === "workout" && <WorkoutTab />}
        {tab === "log" && <WorkoutLogTable token={token} isTrainer={false} />}
        {tab === "diet" && <DietTab />}
        {tab === "progress" && <ProgressTab onUpdate={fetchDashboard} />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "profile" && <ClientProfileTab onUpdate={fetchDashboard} />}
      </div>
    </div>
  );
}

// ==================== OVERVIEW TAB ====================
function OverviewTab({ dashboard, setTab }) {
  const d = dashboard;
  const client = d?.client;
  const payStatus = client?.payment_status || "inactive";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full border-2 border-gg-red/30 overflow-hidden flex items-center justify-center bg-gg-surface shrink-0">
          {client?.profile_image ? (
            <img src={client.profile_image} alt={client.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-gg-red text-lg font-bold">{client?.name?.[0]?.toUpperCase()}</span>
          )}
        </div>
        <div className="min-w-0">
          <h1 className="font-heading text-lg sm:text-xl font-bold truncate" data-testid="welcome-message">Welcome, {client?.name}</h1>
          <div className="flex items-center gap-2">
            <p className="text-zinc-500 text-xs">Your fitness overview</p>
            {client?.training_type && (
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-heading uppercase ${
                client.training_type === "online" ? "bg-blue-500/10 text-blue-400" :
                client.training_type === "nutrition" ? "bg-amber-500/10 text-amber-400" :
                client.training_type === "group" ? "bg-teal-500/10 text-teal-400" :
                "bg-purple-500/10 text-purple-400"
              }`}>{client.training_type === "personal" ? "Personal" : client.training_type === "online" ? "Online" : client.training_type === "group" ? "Group" : "Nutrition"}</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatusCard label="Status" value={payStatus === "active" ? "Active" : payStatus === "expired" ? "Expired" : "Inactive"}
          icon={payStatus === "active" ? CheckCircle : AlertCircle}
          color={payStatus === "active" ? "text-green-400" : "text-red-400"}
          onClick={() => setTab("payments")} />
        <StatusCard label="Workout Plan" value={d?.workout_plan?.plan_name || "Not assigned"}
          icon={Dumbbell} color="text-gg-red" onClick={() => setTab("workout")} />
        <StatusCard label="Diet Plan" value={d?.diet_plan?.plan_name || "Not assigned"}
          icon={Salad} color="text-orange-400" onClick={() => setTab("diet")} />
        <StatusCard label="Last Weight" value={d?.latest_progress?.weight ? `${d.latest_progress.weight} kg` : "No data"}
          icon={Weight} color="text-blue-400" onClick={() => setTab("progress")} />
      </div>

      {d?.workout_plan && (
        <DashCard title="Current Workout Plan" icon={Dumbbell} action={() => setTab("workout")} actionLabel="View Full Plan">
          <p className="text-zinc-400 text-sm">{d.workout_plan.plan_name}</p>
          {d.workout_plan.weeks?.[0] && (
            <p className="text-zinc-500 text-xs mt-1">{d.workout_plan.weeks[0].days?.length || 0} days/week</p>
          )}
        </DashCard>
      )}

      {d?.diet_plan && (
        <DashCard title="Current Diet Plan" icon={Salad} action={() => setTab("diet")} actionLabel="View Full Plan">
          <p className="text-zinc-400 text-sm">{d.diet_plan.plan_name}</p>
          {d.diet_plan.daily_calories > 0 && (
            <p className="text-zinc-500 text-xs mt-1">{d.diet_plan.daily_calories} kcal/day</p>
          )}
        </DashCard>
      )}

      {!d?.workout_plan && !d?.diet_plan && (
        <div className="bg-gg-surface rounded-xl p-5 border border-white/5 text-center">
          <Dumbbell size={28} className="text-zinc-700 mx-auto mb-3" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-400 mb-1">Getting Started</h3>
          <p className="text-zinc-600 text-xs leading-relaxed">Your trainer will assign your workout and diet plans soon. Meanwhile, you can log your workouts in the <button onClick={() => setTab("log")} className="text-gg-red hover:underline">Workout Log</button> tab and update your <button onClick={() => setTab("profile")} className="text-gg-red hover:underline">Profile</button>.</p>
        </div>
      )}
    </motion.div>
  );
}

function StatusCard({ label, value, icon: Icon, color, onClick }) {
  return (
    <button onClick={onClick} className="bg-gg-surface rounded-xl p-3 border border-white/5 hover:border-white/10 transition-all text-left group" data-testid={`status-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} className={color} />
        <p className="text-zinc-500 text-[9px] uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-white text-sm font-semibold truncate">{value}</p>
    </button>
  );
}

function DashCard({ title, icon: Icon, children, action, actionLabel }) {
  return (
    <div className="bg-gg-surface rounded-xl p-4 border border-white/5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={14} className="text-gg-red shrink-0" />
          <h3 className="font-heading text-xs font-bold uppercase tracking-wide truncate">{title}</h3>
        </div>
        {action && (
          <button onClick={action} className="text-gg-red text-[10px] hover:underline flex items-center gap-1 shrink-0">
            {actionLabel} <ChevronRight size={10} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ==================== WORKOUT TAB ====================
function WorkoutTab() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi().get("/client/workout-plan").then(r => setPlan(r.data.plan)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!plan) return <EmptyState icon={Dumbbell} title="No Workout Plan Yet" desc="Your trainer hasn't assigned a workout plan yet. Check back soon!" />;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="workout-section">
      <h2 className="font-heading text-xl font-bold">{plan.plan_name || "Workout Plan"}</h2>
      {plan.weeks?.map((week, wi) => (
        <div key={wi} className="space-y-3">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400">Week {wi + 1}</h3>
          <div className="grid gap-3">
            {week.days?.map((day, di) => (
              <div key={di} className="bg-gg-surface rounded-xl p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar size={14} className="text-gg-red" />
                  <span className="font-heading text-sm font-bold uppercase">{day.name || `Day ${di + 1}`}</span>
                  {day.focus && <span className="text-xs text-zinc-500 ml-auto">{day.focus}</span>}
                </div>
                {day.exercises?.length > 0 ? (
                  <div className="space-y-2">
                    {day.exercises.map((ex, ei) => {
                      const groups = (Array.isArray(ex.set_groups) && ex.set_groups.length > 0)
                        ? ex.set_groups
                        : ((ex.sets || ex.reps) ? [{ sets: ex.sets, reps: ex.reps }] : []);
                      return (
                        <div key={ei} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0 gap-2">
                          <span className="text-sm text-zinc-300 truncate">{ex.name}</span>
                          <span className="text-xs text-zinc-500 shrink-0 text-right">
                            {groups.length > 0
                              ? groups.map((g, i) => (
                                  <span key={i}>{i > 0 && <span className="text-zinc-700">, </span>}{g.sets || "—"} × {g.reps || "—"}</span>
                                ))
                              : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-xs">Rest Day</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </motion.div>
  );
}

// ==================== DIET TAB ====================
// ==================== DIET TAB (READ-ONLY) ====================
const DietRow = ({ label, value, accent }) => (
  <div className="flex justify-between border-b border-white/5 py-1.5">
    <span className="text-zinc-400 text-xs">{label}</span>
    <span className={accent ? "text-gg-red font-medium text-sm" : "text-white text-sm"}>{value || "—"}</span>
  </div>
);

function DietTab() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi().get("/client/diet-plan").then(r => setPlan(r.data.plan)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const downloadPdf = async () => {
    try {
      const resp = await clientApi().get("/client/diet-plan/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "nutrition_plan.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Nutrition plan downloaded!");
    } catch {
      toast.error("Failed to download");
    }
  };

  if (loading) return <Loader />;
  if (!plan) return <EmptyState icon={Salad} title="No Nutrition Plan Yet" desc="Your trainer hasn't assigned a nutrition plan yet. Check back soon!" />;

  const profile = plan.profile || {};
  const calcs = plan.calculations || {};
  const macros = plan.macros || {};
  const mealPlan = plan.meal_plan && plan.meal_plan.length > 0 ? plan.meal_plan : null;

  const totals = (mealPlan || []).reduce((acc, m) => ({
    proteins: acc.proteins + (parseFloat(m.proteins_g) || 0),
    fats: acc.fats + (parseFloat(m.fats_g) || 0),
    carbs: acc.carbs + (parseFloat(m.carbs_g) || 0),
  }), { proteins: 0, fats: 0, carbs: 0 });

  const Row = DietRow;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="diet-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold">{plan.plan_name || "Nutrition Plan"}</h2>
          <p className="text-zinc-500 text-xs mt-1 uppercase tracking-widest font-heading">Personalised Nutrition</p>
        </div>
        <Button onClick={downloadPdf} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="download-diet-pdf">
          <Download size={14} className="mr-2" /> PDF
        </Button>
      </div>

      {/* Case Study */}
      {(profile.height_feet || profile.weight_kg || profile.goal) && (
        <div className="bg-gg-surface rounded-xl p-4 border border-white/5">
          <p className="font-heading text-[10px] uppercase tracking-widest text-gg-red mb-3">1 · Member Profile</p>
          <Row label="Height" value={profile.height_feet ? `${profile.height_feet} ft ${profile.height_inches || 0} in` : ""} />
          <Row label="Gender" value={profile.gender} />
          <Row label="Weight" value={profile.weight_kg ? `${profile.weight_kg} kg` : ""} />
          <Row label="Visual Assessment" value={profile.visual_assessment} />
          <Row label="Intensity" value={profile.intensity} />
          <Row label="Dietary Orientation" value={profile.dietary_orientation} />
          <Row label="Workout Time" value={profile.workout_time} />
          <Row label="Wake Up Time" value={profile.wake_up_time} />
          <Row label="Sleep Time" value={profile.sleep_time} />
          {profile.goal && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <p className="text-zinc-400 text-xs uppercase tracking-wider font-heading mb-1">Goal</p>
              <p className="text-white text-sm">{profile.goal}</p>
            </div>
          )}
        </div>
      )}

      {/* Calculations */}
      {(calcs.height_cms || calcs.ibw_kgs) && (
        <div className="bg-gg-surface rounded-xl p-4 border border-white/5">
          <p className="font-heading text-[10px] uppercase tracking-widest text-gg-red mb-3">2 · Calculations</p>
          <Row label="Height (cms)" value={calcs.height_cms} accent />
          <Row label="IBW (kgs)" value={calcs.ibw_kgs} accent />
          <Row label="IBW range" value={`${calcs.ibw_range_low} – ${calcs.ibw_range_high} kg`} accent />
          <Row label="Body Fat %" value={calcs.body_fat_pct ? `${calcs.body_fat_pct}%` : ""} />
          <Row label="LBM %" value={calcs.lbm_pct ? `${calcs.lbm_pct}%` : ""} />
          <Row label="LBM (kgs)" value={calcs.lbm_kgs} accent />
        </div>
      )}

      {/* Macros */}
      {(macros.proteins_g || macros.fats_g) && (
        <div className="bg-gg-surface rounded-xl p-4 border border-white/5">
          <p className="font-heading text-[10px] uppercase tracking-widest text-gg-red mb-3">3 · Macros</p>
          <Row label="Proteins / day" value={macros.proteins_g ? `${macros.proteins_g} g` : ""} accent />
          <Row label="Fats / day" value={macros.fats_g ? `${macros.fats_g} g (+ up to 50 g invisible)` : ""} accent />
          <Row label="Carbs / day" value="< 30 g (incidental)" />
          <Row label="Meals" value={macros.meals_count} />
          <Row label="Protein / meal" value={macros.protein_per_meal ? `${macros.protein_per_meal} g` : ""} accent />
        </div>
      )}

      {/* Meal Plan */}
      {mealPlan && mealPlan.length > 0 && (
        <div className="bg-gg-surface rounded-xl p-4 border border-white/5">
          <p className="font-heading text-[10px] uppercase tracking-widest text-gg-red mb-3">4 · Meal Plan</p>
          <div className="space-y-3">
            {mealPlan.map((meal, i) => (
              <div key={i} className="bg-gg-black rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock size={12} className="text-orange-400" />
                    <span className="font-heading text-sm font-bold uppercase">{meal.name}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    <span className="mr-2">P:<span className="text-white ml-0.5">{meal.proteins_g || "—"}g</span></span>
                    <span className="mr-2">F:<span className="text-white ml-0.5">{meal.fats_g || "—"}g</span></span>
                    <span>C:<span className="text-white ml-0.5">{meal.carbs_g || "—"}g</span></span>
                  </div>
                </div>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap">{meal.food_items}</p>
                {meal.post_meal && <p className="text-zinc-500 text-xs mt-2"><span className="text-gg-red">+</span> {meal.post_meal}</p>}
              </div>
            ))}
          </div>
          {/* Totals */}
          <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-[1fr_auto_auto_auto] gap-x-3 text-xs items-baseline">
            <span className="font-heading uppercase tracking-wider text-zinc-400 text-[10px] sm:text-xs">Total</span>
            <span className="whitespace-nowrap">P: <span className="text-white">{totals.proteins.toFixed(1)} g</span></span>
            <span className="whitespace-nowrap">F: <span className="text-white">{totals.fats.toFixed(1)} g</span></span>
            <span className="whitespace-nowrap">C: <span className="text-white">{totals.carbs.toFixed(1)} g</span></span>
          </div>
          {plan.bedtime_supplement && (
            <p className="text-zinc-400 text-xs mt-2"><span className="text-gg-red font-heading uppercase tracking-wider text-[10px]">Bedtime:</span> {plan.bedtime_supplement}</p>
          )}
        </div>
      )}

      {/* Legacy fallback: show simple meals[] if no meal_plan exists */}
      {!mealPlan && plan.meals?.length > 0 && (
        <div className="space-y-3">
          {plan.meals.map((meal, i) => (
            <div key={i} className="bg-gg-surface rounded-xl p-4 border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-orange-400" />
                  <span className="font-heading text-sm font-bold uppercase">{meal.name}</span>
                </div>
                {meal.time && <span className="text-xs text-zinc-500">{meal.time}</span>}
              </div>
              <p className="text-zinc-400 text-sm">{meal.items}</p>
            </div>
          ))}
        </div>
      )}

      {plan.notes && (
        <div className="bg-gg-surface rounded-xl p-4 border border-white/5">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={14} className="text-zinc-400" />
            <span className="text-xs font-heading uppercase tracking-wider text-zinc-400">Notes</span>
          </div>
          <p className="text-zinc-300 text-sm">{plan.notes}</p>
        </div>
      )}
    </motion.div>
  );
}

// ==================== PROGRESS TAB (READ-ONLY) ====================
function ProgressTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi().get("/client/progress").then(r => setEntries(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="progress-section">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">Progress Tracking</h2>
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-heading">View only</span>
      </div>

      {entries.length === 0 ? (
        <EmptyState icon={TrendingUp} title="No Progress Entries Yet" desc="Your trainer will log your progress measurements here after each check-in." />
      ) : (
        <div className="space-y-3">
          {entries.map((entry, i) => (
            <div key={entry.id || i} className="bg-gg-surface rounded-xl p-4 border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-500">{new Date(entry.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                {entry.added_by === "trainer" && <span className="text-[10px] bg-gg-red/20 text-gg-red px-2 py-0.5 rounded-full">By Trainer</span>}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {entry.weight && <MiniStat label="Weight" value={`${entry.weight} kg`} />}
                {entry.body_fat && <MiniStat label="Body Fat" value={`${entry.body_fat}%`} />}
                {entry.chest && <MiniStat label="Chest" value={`${entry.chest} cm`} />}
                {entry.waist && <MiniStat label="Waist" value={`${entry.waist} cm`} />}
                {entry.hips && <MiniStat label="Hips" value={`${entry.hips} cm`} />}
                {entry.arms && <MiniStat label="Arms" value={`${entry.arms} cm`} />}
                {entry.thighs && <MiniStat label="Thighs" value={`${entry.thighs} cm`} />}
              </div>
              {entry.notes && <p className="text-zinc-400 text-xs mt-2">{entry.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-zinc-600 uppercase">{label}</p>
      <p className="text-sm text-white font-semibold">{value}</p>
    </div>
  );
}

// ==================== PAYMENTS TAB ====================
function PaymentsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi().get("/client/payments").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="payments-section">
      <h2 className="font-heading text-xl font-bold">Payment Status</h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gg-surface rounded-xl p-5 border border-white/5">
          <p className="text-[10px] uppercase text-zinc-500 mb-1">Status</p>
          <div className="flex items-center gap-2">
            {data?.status === "active" ? <CheckCircle size={18} className="text-green-400" /> : <AlertCircle size={18} className="text-red-400" />}
            <span className={`font-heading text-lg font-bold uppercase ${data?.status === "active" ? "text-green-400" : "text-red-400"}`}>
              {data?.status || "Inactive"}
            </span>
          </div>
        </div>
        <div className="bg-gg-surface rounded-xl p-5 border border-white/5">
          <p className="text-[10px] uppercase text-zinc-500 mb-1">Current Plan</p>
          <p className="text-white font-semibold">{data?.plan_name || "None"}</p>
        </div>
        <div className="bg-gg-surface rounded-xl p-5 border border-white/5">
          <p className="text-[10px] uppercase text-zinc-500 mb-1">Next Renewal</p>
          <p className="text-white font-semibold">{data?.next_renewal || "N/A"}</p>
        </div>
      </div>

      {data?.history?.length > 0 && (
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-3">Payment History</h3>
          <div className="space-y-2">
            {data.history.map((rec, i) => (
              <div key={rec.id || i} className="bg-gg-surface rounded-xl p-4 border border-white/5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">{rec.plan_name || "Payment"}</p>
                  <p className="text-xs text-zinc-500">{new Date(rec.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                  {rec.notes && <p className="text-xs text-zinc-500 mt-0.5">{rec.notes}</p>}
                </div>
                <span className="text-gg-red font-heading font-bold">{rec.amount > 0 ? `₹${rec.amount}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!data?.history || data.history.length === 0) && (
        <EmptyState icon={CreditCard} title="No Payment History" desc="Payment records will appear here once your trainer updates them." />
      )}
    </motion.div>
  );
}

// ==================== SHARED ====================
function Loader() {
  return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-gg-red border-t-transparent rounded-full animate-spin" /></div>;
}

function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="text-center py-12">
      <Icon size={40} className="text-zinc-700 mx-auto mb-4" />
      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">{title}</h3>
      <p className="text-zinc-600 text-xs max-w-xs mx-auto">{desc}</p>
    </div>
  );
}


function calcAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age > 0 ? age : null;
}

function ClientProfileTab({ onUpdate }) {
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [trainingType, setTrainingType] = useState("personal");
  const [profileImage, setProfileImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    clientApi().get("/client/me").then(({ data }) => {
      setProfile(data);
      setName(data.name || "");
      setPhone(data.phone || "");
      setDob(data.date_of_birth || "");
      setTrainingType(data.training_type || "personal");
      setProfileImage(data.profile_image || "");
    }).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await clientApi().post("/client/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfileImage(data.url);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await clientApi().put("/client/me", {
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        date_of_birth: dob || undefined,
        training_type: trainingType,
        profile_image: profileImage || undefined
      });
      toast.success("Profile updated!");
      if (name.trim()) {
        localStorage.setItem("gg_client_name", name.trim());
      }
      onUpdate();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const age = calcAge(dob);

  if (!profile) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-gg-red border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <h2 className="font-heading text-xl font-bold uppercase tracking-wide">My Profile</h2>

      <div className="bg-gg-surface border border-white/10 rounded-xl p-6 space-y-5">
        {/* Profile Image */}
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gg-black border-2 border-white/10 overflow-hidden flex items-center justify-center">
              {profileImage ? (
                <img src={profileImage} alt={name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-gg-red text-3xl font-bold">{name?.[0]?.toUpperCase() || "?"}</span>
              )}
            </div>
            <label className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gg-red flex items-center justify-center cursor-pointer hover:bg-gg-red-hover transition-colors" data-testid="client-upload-photo">
              <Camera size={14} className="text-white" />
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            </label>
          </div>
          <div>
            <p className="text-white font-heading text-lg font-bold">{name}</p>
            <p className="text-zinc-500 text-xs">{profile.email}</p>
            {age && <p className="text-zinc-400 text-xs mt-0.5">{age} years old</p>}
            {uploading && <p className="text-gg-red text-[10px] mt-1">Uploading...</p>}
          </div>
        </div>

        {/* Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Full Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="client-profile-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Phone</label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="client-profile-phone" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Date of Birth{age ? ` (${age}y)` : ""}</label>
            <Input type="date" value={dob} onChange={e => setDob(e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="client-profile-dob" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Training Type</label>
            <select value={trainingType} onChange={e => setTrainingType(e.target.value)} className="w-full bg-gg-black border border-white/10 text-white rounded-lg h-10 px-3 text-sm" data-testid="client-profile-training">
              <option value="personal">Personal Training</option>
              <option value="online">Online Training</option>
              <option value="nutrition">Nutrition</option>
              <option value="group">Group Training</option>
            </select>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg font-heading uppercase tracking-widest text-xs" data-testid="client-save-profile">
          <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save Profile"}
        </Button>
      </div>
    </motion.div>
  );
}
